<template>
  <div id="app">
    <loading-view v-if="$store.state.loading" />

    <md-dialog
      :md-active.sync="$store.state.notification.show"
      :md-close-on-esc="!$store.state.notification.isAuthError"
      :md-click-outside-to-close="!$store.state.notification.isAuthError">
      <md-dialog-content>
        {{ $store.state.notification.message }}
      </md-dialog-content>
      <md-dialog-actions>
        <md-button v-if="$store.state.notification.isAuthError" class="md-primary" @click="hideNotification();logout();">{{ $t('logout') }}</md-button>
        <md-button v-else class="md-primary" @click="hideNotification()">{{ $t('close') }}</md-button>
      </md-dialog-actions>
    </md-dialog>

    <div v-if="!$auth.loading && $auth.isAuthenticated && $store.state.user.client">
      <keep-alive>
        <router-view />
      </keep-alive>
    </div>

    <footer class="md-layout md-alignment-center-space-between">
      <div class="md-layout-item md-size-70 md-small-hide md-medium-hide">
        {{ $t('copyright') }}
        <md-icon>link</md-icon>
        <a class="mr-1" href="https://tema-q.de/impressum/" target="_blank">{{ $t('legal_notice') }}</a>
        <md-icon>link</md-icon>
        <a class="mr-1" href="https://tema-q.de/datenschutzerklaerung/" target="_blank">{{ $t('privacy_policy') }}</a>
      </div>
      <div class="md-layout-item md-size-30 md-medium-size-100 ta-r">
        <div style="display:inline-block">
          <md-field id="lang-select">
            <md-select
              v-model="$i18n.locale"
              @md-selected="setLocale"
              md-dense
              id="lang">
              <span id="option-de" class="lang-option"><md-option value="de">{{ $t('german') }}</md-option></span>
              <span id="option-en" class="lang-option"><md-option value="en">{{ $t('english') }}</md-option></span>
            </md-select>
          </md-field>
        </div>
        <div style="display:inline-block">
          &copy; {{ new Date().getFullYear() }} TEMA-Q GmbH
        </div>
      </div>
    </footer>
  </div>
</template>

<script>

export default {
  name: 'App',
  mounted () {
    this.setLocaleFlag()
    // Init idle timer.
    this.$auth.initIdleTimer()
  },
  async created () {
    const $vm = this
    // Detect when browser is being resized
    $vm.onResize()
    // Add resize listeners.
    window.addEventListener('resize', $vm.onResize)
  },
  beforeDestroy () {
    // Remove resize listeners.
    window.removeEventListener('resize', this.onResize)
    // Destroy idle timer.
    this.$auth.destroyIdleTimer()
  },
  methods: {
    logout () {
      this.$auth.logout()
    },
    hideNotification () {
      this.$store.dispatch('setNotification', { show: false })
    },
    onResize () {
      const $vm = this
      // Set screen size specific options
      // See themes.scss for screen size values
      if (window.innerWidth <= 600) {
        $vm.$store.dispatch('setScreenSize', 'xsmall')
      } else if (window.innerWidth <= 960) {
        $vm.$store.dispatch('setScreenSize', 'small')
        $vm.$store.dispatch('setSidebarActive', false)
        $vm.$store.dispatch('setSidebarType', null)
      } else {
        $vm.$store.dispatch('setScreenSize', 'desktop')
        $vm.$store.dispatch('setSidebarType', 'clipped')
      }
    },
    setLocale (locale) {
      localStorage.setItem('locale', locale)
      this.setLocaleFlag(locale)
    },
    setLocaleFlag (locale = null) {
      const lang = locale || this.$i18n.locale
      const input = (document.querySelector('#lang-select')) ? document.querySelector('#lang-select').querySelector('.md-select') : null
      if (input) {
        switch (lang) {
          case 'de':
            input.id = 'input-de'
            break
          case 'en':
            input.id = 'input-en'
            break
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "styles/theme";

@import "~vue-material/dist/theme/engine";
@include md-register-theme(
  "default",
  (primary: $primary, accent: $accent, theme: light)
);
@include md-register-theme(
  "toolbar",
  (primary: $grey, accent: $primary, theme: dark)
);
@import "~vue-material/dist/theme/all";

// Toolbar theme
// Adjust color of selected list items
.md-list.md-theme-toolbar .md-selected .md-list-item-content {
  color: #aaa;
}

html,
body,
#app .md-app-scroller,
#content {
  background-color: $background;
  outline: 0;
}

footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: $footer-height;
  background: $background;
  padding: 0 $content-padding;
  z-index: 11;
  font-size: 13px;
  color: #333;
  border-top: 1px solid $border;
}

#app .md-app {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  font-family: "Roboto";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#sidebar {
  width: $sidebar-width;
  border-right: 1px solid $border;
  .md-toolbar {
    background: $grey-medium;
    border-bottom: 1px solid $border;
    min-height: 70px;
    padding-right: $content-padding;
    padding-left: $content-padding;
    span {
      font-size: 21px;
      font-weight: 500;
      color: $heading-color !important;
    }
  }
  .md-content {
    padding: 0 !important;
  }
  .md-app-scroller {
    background-color: $grey-light !important;
  }
  .md-content,
  .md-list {
    background-color: transparent !important;
  }
}
#sidebar-content {
  padding-bottom: $footer-height;
}

#content {
  left: $sidebar-width !important;
  bottom: $footer-height !important;
  @media screen and (max-width: $small) {
    left: 0 !important;
  }
  @media screen and (min-width: $xsmall) and (max-width: $small) {
    top: $navbar-height !important;
  }
}

.md-content {
  &:not(.md-table):not(.md-table-content) {
    border: none !important;
    @media screen and (max-width: $small) {
      padding: $content-padding-mobile !important;
    }
  }
}

.content-padding {
  padding: $content-padding;
}
.no-content-padding {
  position: relative;
  margin-right: -$content-padding;
  margin-left: -$content-padding;
  @media screen and (max-width: $small) {
    margin-right: -$content-padding-mobile;
    margin-left: -$content-padding-mobile;
  }
}

.md-gutter {
  margin-right: -$content-padding !important;
  margin-left: -$content-padding !important;
  @media screen and (max-width: $small) {
    margin-right: 0 !important;
    margin-left: 0 !important;
  }
}

thead {
  background: #f2f2f2;
}

// Dialog
.md-overlay {
  background: rgba(255,255,255,0.8) !important;
  top: $navbar-height !important;
}

// Language select
#lang-select {
  width: 125px !important;
  min-height: auto !important;
  margin: 0 10px 0 0 !important;
  padding: 0 !important;
  input {
    font-size: 14px !important;
    padding-left: 33px !important;
  }
  .md-select {
    background-position: 5px 10px;
    background-repeat: no-repeat;
  }
}
.lang-option {
  background-position: 16px 14px;
  background-repeat: no-repeat;
  .md-list-item-text {
    padding-left: 35px;
  }
}
#input-de,
#option-de {
  background-image: url('./assets/flags/de.png');
}
#input-en,
#option-en {
  background-image: url('./assets/flags/en.png');
}

// Headings
h1,
h2,
h3,
h4,
h5,
h6 {
  font-weight: 500;
  color: $heading-color;
}
h1 {
  font-size: 24px;
  line-height: 32px;
}
h2 {
  font-size: 22px;
  line-height: 30px;
}

// Helper classes
.ta-r {
  text-align: right !important;
}
.ta-c {
  text-align: center !important;
}
.pos-r {
  position: relative !important;
}
.pos-a {
  position: absolute !important;
}
.pos-f {
  position: fixed !important;
}
.mt-0 {
  margin-top: 0 !important;
}
.mb-0 {
  margin-bottom: 0 !important;
}
.ml-0 {
  margin-left: 0 !important;
}
.mr-0 {
  margin-right: 0 !important;
}
.mt-1 {
  margin-top: 8px !important;
}
.mb-1 {
  margin-bottom: 8px !important;
}
.ml-1 {
  margin-left: 8px !important;
}
.mr-1 {
  margin-right: 8px !important;
}
.mt-2 {
  margin-top: 16px !important;
}
.mb-2 {
  margin-bottom: 16px !important;
}
.ml-2 {
  margin-left: 16px !important;
}
.mr-2 {
  margin-right: 16px !important;
}
.mt-3 {
  margin-top: 24px !important;
}
.mb-3 {
  margin-bottom: 24px !important;
}
.ml-3 {
  margin-left: 24px !important;
}
.mr-3 {
  margin-right: 24px !important;
}
.mt-4 {
  margin-top: 32px !important;
}
.mb-4 {
  margin-bottom: 32px !important;
}
.ml-4 {
  margin-left: 32px !important;
}
.mr-4 {
  margin-right: 32px !important;
}
.mt-5 {
  margin-top: 48px !important;
}
.mb-5 {
  margin-bottom: 48px !important;
}
.ml-4 {
  margin-left: 48px !important;
}
.mr-4 {
  margin-right: 48px !important;
}
.pt-0 {
  padding-top: 0px !important;
}
.pb-0 {
  padding-bottom: 0px !important;
}
.pl-0 {
  padding-left: 0px !important;
}
.pr-0 {
  padding-right: 0px !important;
}
.pt-1 {
  padding-top: 8px !important;
}
.pb-1 {
  padding-bottom: 8px !important;
}
.pl-1 {
  padding-left: 8px !important;
}
.pr-1 {
  padding-right: 8px !important;
}
.text-smaller {
  font-size: 0.94em;
}
.text-italic {
  font-style: italic;
}
.muted {
  opacity: 0.7;
}
</style>
